// ─────────────────────────────────────────────────────────────────────────────
// Sale Strength — deterministic 0–100 completeness rating for sale/discount
// cards, following the offering-strength pattern (shared/offer-strength.ts).
//
// The score grades DEPTH across the sale model, not just presence: developed
// copy fields earn full credit while stubs earn half. The same computation
// drives the card gauge and the "Sale Ready" badge, so they can never
// disagree.
//
// Hero imagery doesn't live on a single sale column (the card resolves a
// fallback chain: primary product photo → first appliesTo product photo →
// primary offer's photo chain) — the card passes the resolved `hasHeroPhoto`
// flag in. When absent that dimension simply scores 0.
// ─────────────────────────────────────────────────────────────────────────────

export interface SaleStrengthInput {
  name?: string | null;
  saleType?: string | null;
  discountValue?: string | null;
  appliesTo?: unknown;
  primaryProductId?: string | null;
  primaryOfferId?: string | null;
  promotionSummary?: string | null;
  customerFacingCopy?: string | null;
  whyThisSaleExists?: string | null;
  urgencyMechanism?: string | null;
  expirationDate?: Date | string | null;
  promoCode?: string | null;
  termsConditions?: string | null;
  ctaLanguage?: string | null;
  creativeNotes?: string | null;
  sketchImageUrl?: string | null;
  // Resolved by the card from the hero-photo fallback chain (primary product
  // photo → first appliesTo product photo → primary offer's photo chain).
  hasHeroPhoto?: boolean | null;
}

/** Score at or above which a sale is considered "ready" (badge + copy). */
export const SALE_STRENGTH_READY_THRESHOLD = 70;

export function isSaleStrengthReady(score: number): boolean {
  return score >= SALE_STRENGTH_READY_THRESHOLD;
}

/** One-line readiness caption shown under the detail page's strength ring
 *  (mirrors offerStrengthCaption in shared/offer-strength.ts). */
export function saleStrengthCaption(score: number): string {
  if (score >= 90) return "Exceptionally complete. Ready to run.";
  if (score >= SALE_STRENGTH_READY_THRESHOLD) return "Strong promotion. Ready to run.";
  if (score >= 40) return "Solid foundation. Fill the gaps to strengthen it.";
  return "Early draft. Add detail to build strength.";
}

/** Graded credit for a free-text field: 0 empty, half for a stub (<40 chars),
 *  full for developed text. */
function textScore(value: unknown, weight: number): number {
  const len = typeof value === "string" ? value.trim().length : 0;
  if (len === 0) return 0;
  return len >= 40 ? weight : weight / 2;
}

function hasText(value: unknown): boolean {
  return typeof value === "string" && value.trim().length > 0;
}

/** True when the appliesTo jsonb targets something concrete: a whole-brand
 *  scope, or a scoped list with at least one id. */
export function appliesToIsMeaningful(v: unknown): boolean {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  const scope = typeof o.scope === "string" ? o.scope : "";
  if (scope === "brand") return true;
  const productIds = Array.isArray(o.productIds) ? o.productIds.filter(hasText) : [];
  const offerIds = Array.isArray(o.offerIds) ? o.offerIds.filter(hasText) : [];
  return productIds.length > 0 || offerIds.length > 0;
}

/**
 * Deterministic 0–100 sale strength.
 *
 * Weight budget (sums to 100):
 *  - Identity (8): name+saleType 4, discountValue 4.
 *  - Copy (40): promotionSummary 12, customerFacingCopy 10,
 *    whyThisSaleExists 8, ctaLanguage 6, creativeNotes 4 — text fields
 *    graded by developed length (ctaLanguage counts full at any length,
 *    CTAs are short by nature).
 *  - Mechanics (26): real urgency mechanism 6, expirationDate 4,
 *    promoCode 8, termsConditions 8.
 *  - Targeting (16): meaningful appliesTo 8, primary product or offer 8.
 *  - Imagery (10): hero photo 6, quote-strip sketch 4.
 */
export function computeSaleStrength(s: SaleStrengthInput): number {
  let score = 0;

  // Identity — 8
  if ((s.name ?? "").trim() && (s.saleType ?? "").trim()) score += 4;
  if ((s.discountValue ?? "").trim()) score += 4;

  // Copy — 40
  score += textScore(s.promotionSummary, 12);
  score += textScore(s.customerFacingCopy, 10);
  score += textScore(s.whyThisSaleExists, 8);
  if (hasText(s.ctaLanguage)) score += 6; // any CTA copy counts full
  score += textScore(s.creativeNotes, 4);

  // Mechanics — 26
  const urgency = (s.urgencyMechanism ?? "").trim();
  if (urgency && urgency !== "no_urgency") score += 6;
  if (s.expirationDate != null && String(s.expirationDate).trim() !== "") score += 4;
  if ((s.promoCode ?? "").trim()) score += 8;
  score += textScore(s.termsConditions, 8);

  // Targeting — 16
  if (appliesToIsMeaningful(s.appliesTo)) score += 8;
  if ((s.primaryProductId ?? "").trim() || (s.primaryOfferId ?? "").trim()) score += 8;

  // Imagery — 10
  if (s.hasHeroPhoto) score += 6;
  if ((s.sketchImageUrl ?? "").trim()) score += 4;

  return Math.max(0, Math.min(100, Math.round(score)));
}
